import i18n from 'localization/i18nextConf';
import toast from 'react-hot-toast';
import Compressor from 'compressorjs';
import useAuthStore, { updateUserInfo } from 'store/auth';
import { onlySpecialCharsNotAllowRegex, USER_TYPES } from 'constants';
import { createCanvas, loadImage } from 'canvas';
import { ARRAY_FIELDS, SAVING_FIELDS } from 'constants/resumeForm';
import moment from 'moment';
import { t } from 'i18next';
export const activeLocale = i18n.language === 'en' ? 'en' : i18n.language;

export const isSeeker = () => {
	const authUser: any = useAuthStore((state) => state.userInfo.user);
	return authUser?.userType === USER_TYPES.SEEKER;
};

export const isCompany = () => {
	const authUser: any = useAuthStore((state) => state.userInfo.user);
	return authUser?.userType === USER_TYPES.COMPANY;
};

export const generateYearOptions = () => {
	const currentYear = new Date().getFullYear();
	const startYear = 1950;
	const years = [];

	for (let year = startYear; year <= currentYear; year++) {
		years.push({ value: year.toString(), label: year.toString() });
	}
	return [{ value: '', label: t('PlaceHolder.selectYear') }, ...years];
};

export const extractUsername = (urlOrUsername: string): string | undefined => {
	if (!urlOrUsername) return;

	if (urlOrUsername.startsWith('https://') || urlOrUsername.startsWith('http://')) {
		const parts = urlOrUsername.split('/');
		urlOrUsername = parts[parts.length - 1];
	}

	if (!urlOrUsername.startsWith('@')) {
		return `@${urlOrUsername}`;
	}

	return urlOrUsername;
};

export const getArrayKeyWise = (arrayData: any, findByKey: string) => {
	let data: string[] = [];
	if (arrayData?.length > 0) {
		if (['cityId', 'stateId', 'countryId'].includes(findByKey)) {
			data = arrayData
				.map((data: any) => data?.[findByKey])
				.filter((value: any) => value !== null && value !== undefined && value > 0);
		} else {
			data = arrayData
				.map((data: any) => data?.[findByKey])
				.filter((value: any) => value !== null && value !== undefined);
		}
	}
	return data;
};

export const getDateString = (date: any) => {
	if (date) {
		const monthNames = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		];
		const dateFormat = new Date(date);
		const month = monthNames[dateFormat.getMonth()];
		const year = dateFormat.getFullYear();

		return `${month} ${year}`;
	}
	return '';
};

// Format the year as date formate
export const formatDateWithYear = (year: number | null) => {
	if (!year) return '';
	return new Date(year, 0, 1, 0, 0, 0);
};

export const getLanguageProficiencies = [
	{
		label: 'Beginner',
		value: 'Beginner',
	},
	{
		label: 'Intermediate',
		value: 'Intermediate',
	},
	{
		label: 'Proficient',
		value: 'Proficient',
	},
];

export const isValidFileType = (
	file: File,
	validTypes = [
		'application/pdf',
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	],
) => {
	if (validTypes.includes(file.type)) {
		return true;
	} else {
		toast.error('Please upload a valid file type');
		return false;
	}
};

export const isValidFileSize = (file: File, maxSize = 10 * 1024 * 1024) => {
	if (file.size <= maxSize) {
		return true;
	} else {
		toast.error(`File size should be less than ${maxSize / (1024 * 1024)} MB.`);
		return false;
	}
};

export const unitNumber = (value: number, floatDig: number = 1) => {
	let numValue = Number(value);
	if (numValue > 0) {
		const units = ['', 'K', 'M', 'B', 'T'];
		let unitIndex = 0;

		while (numValue >= 1000 && unitIndex < units.length - 1) {
			numValue /= 1000;
			unitIndex++;
		}

		// Format the number with the specified decimal places
		let formattedValue = numValue.toFixed(floatDig);

		// Remove unnecessary trailing .0 if present
		if (formattedValue.endsWith('.0')) {
			formattedValue = formattedValue.slice(0, -2);
		}

		return formattedValue + units[unitIndex];
	} else {
		return '0';
	}
};

export const updateAuthValues = (data: any) => {
	let newData: any = {};
	if (data) {
		newData = {
			profilePicture: data?.profilePicture || '',
			isPhoneNumberVerified: data?.isPhoneNumberVerified || '',
			auth2Factor: data?.auth2Factor || '',
			coverPicture: data?.coverPicture || '',
			address: data?.address || '',
			designation: data?.designation || '',
			firstName: data?.firstName || '',
			lastName: data?.lastName || '',
			about: data?.about || '',
			totalExperience: data?.totalExperience || '',
			videoResume: data?.videoResume || '',
			openToWork: data.openToWork || '',
			bio: data?.bio || '',
			followings: data?.followings || '',
			connection: data?.connection || '',
			followers: data?.followers || '',
			phoneNumber: data?.phoneNumber || '',
			countryCode: data?.countryCode || '',
			cityId: data?.cityId || data?.city?.id || '',
			stateId: data?.stateId || data?.state?.id || '',
			countryId: data?.countryId || data?.city?.country || '',
			resume: data?.resume || {},
			companyName: data?.companyName || '',
			experience: data?.experience || [],
			education: data?.education || [],
			certification: data?.certification || [],
			projectPublication: data?.projectPublication || [],
			accountVisibility: data?.accountVisibility || {},
		};
	}
	return newData;
};

export const updateCompanyAuthValues = (data: any) => {
	let newData: any = {};
	if (data) {
		newData = {
			profilePicture: data?.profilePicture || '',
			isPhoneNumberVerified: data?.isPhoneNumberVerified || '',
			auth2Factor: data?.auth2Factor || '',
			coverPicture: data?.coverPicture || '',
			address: data?.address || '',
			designation: data?.designation || '',
			companyCEOName: data?.companyCEOName || '',
			companyDescription: data?.companyDescription || '',
			companyFoundedYear: data?.companyFoundedYear || '',
			companySize: data?.companySize || '',
			followings: data?.followings || '',
			connection: data?.connection || '',
			followers: data?.followers || '',
			phoneNumber: data?.phoneNumber || '',
			countryCode: data?.countryCode || '',
			cityId: data?.cityId || data?.city?.id || '',
			stateId: data?.cityId || data?.state?.id || '',
			countryId: data?.cityId || data?.city?.country || '',
			companyName: data?.companyName || '',
			industryName: data?.industryName || '',
		};
	}
	return newData;
};

export const getCreatedDifferenceInBestUnit = (dateStr: any) => {
	const date: any = new Date(dateStr);
	const now: any = new Date();
	const diffMilliseconds = now - date;

	if (diffMilliseconds < 0) {
		return 'Just Now';
	}

	const timeUnits = [
		{ unit: 'second', value: Math.floor(diffMilliseconds / 1000), threshold: 60 },
		{ unit: 'minute', value: Math.floor(diffMilliseconds / (1000 * 60)), threshold: 60 },
		{ unit: 'hour', value: Math.floor(diffMilliseconds / (1000 * 60 * 60)), threshold: 24 },
		{ unit: 'day', value: Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24)), threshold: 7 },
		{ unit: 'week', value: Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24 * 7)), threshold: 4 },
		{
			unit: 'month',
			value: Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24 * 30.44)),
			threshold: 12,
		},
		{
			unit: 'year',
			value: Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24 * 365.25)),
			threshold: Infinity,
		},
	];

	for (const { unit, value, threshold } of timeUnits) {
		if (unit === 'day' && value === 0) {
			// Special case: if days are 0, display in seconds or "Just now"
			const secondsValue = Math.floor(diffMilliseconds / 1000);
			return secondsValue > 0
				? `${secondsValue} second${secondsValue !== 1 ? 's' : ''} ago`
				: 'Just now';
		}

		if (unit === 'month' && value === 0) {
			// Special case: if months are 0, display in days
			const daysValue = Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24));
			return `${daysValue} day${daysValue !== 1 ? 's' : ''} ago`;
		}

		if (value > 0 && value < threshold) {
			return `${value} ${unit}${value !== 1 ? 's' : ''} ago`;
		}
	}

	// Fallback: if the loop ends without returning, it means diffMilliseconds is very small, or the dates are almost equal.
	return 'Just now';
};

export const getTimeDifferenceInBaseShortUnit = (dateStr: any) => {
	const utcTime = moment.utc(dateStr, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
	const localTime = utcTime.local();
	return moment(localTime.format('YYYY-MM-DD HH:mm:ss.SSSSSS')).fromNow();
};

export const getFileExtension = (fileName: string) => {
	return fileName.split('.').pop() || '';
};

export const getFileType = (mimeType: string): string => {
	if (!mimeType) {
		console.error('Invalid mimeType provided.');
		return '';
	}

	const fileTypes: Record<string, string> = {
		'image/': 'image',
		'video/': 'video',
		'audio/': 'audio',
		'text/': 'doc',
		'application/pdf': 'doc',
		'application/msword': 'doc',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
		'application/vnd.ms-excel': 'doc',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'doc',
		'application/vnd.ms-powerpoint': 'doc',
		'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'doc',
	};

	for (const prefix in fileTypes) {
		if (mimeType.startsWith(prefix)) {
			return fileTypes[prefix];
		}
	}

	return '';
};

export const imageBucketUrl = async (
	file: any,
	translate: any,
	fileType: string,
	fileSize: number,
	bucketUrlMutation: any,
	uploadFileMutation: any,
	uploadFor: string = 'PROFILE_IMAGE',
) => {
	const maxSize = fileSize * 1024 * 1024; // 5MB
	if (!file || !file?.type || !file?.size) {
		return '';
	}

	if (file && file.size > maxSize) {
		toast.error(translate('Validation.imageSize5MB'));
		return false;
	}
	const response = await bucketUrlMutation({
		type: uploadFor,
		extension: [file.name.split('.').pop()],
		contentType: [file.type],
		originalFileName: [file.name],
	});

	const signUrlData = response?.data?.length && response?.data?.[0];
	if (signUrlData) {
		const requestOptions = {
			method: 'PUT',
			headers: {
				'Content-Type': file?.type,
			},
			body: file,
			redirect: 'follow',
		};

		await uploadFileMutation({
			url: signUrlData?.url,
			requestOptions,
		});
		return signUrlData?.location;
	}
	toast.error(translate('Validation.pleaseTryAgain'));
	return '';
};

export const removeLocationsFromDropdown = (formikData: any, item: any) => {
	let updatedData = [];
	if (item.cityId > 0) {
		formikData.setFieldValue(
			'cityIds',
			formikData.values.cityIds.filter((loc: any) => loc !== item?.cityId),
		);
		updatedData = formikData.values.locations.filter((loc: any) => loc.cityId !== item?.cityId);
	} else if (item.stateId > 0) {
		formikData.setFieldValue(
			'stateIds',
			formikData.values.stateIds.filter((loc: any) => loc !== item?.stateId),
		);
		updatedData = formikData.values.locations.filter((loc: any) => loc.stateId !== item?.stateId);
	} else if (item.countryId > 0) {
		formikData.setFieldValue(
			'countryIds',
			formikData.values.countryIds.filter((loc: any) => loc !== item?.countryId),
		);
		updatedData = formikData.values.locations.filter(
			(loc: any) => loc.countryId !== item?.countryId,
		);
	}

	formikData.setFieldValue('locations', updatedData);
};

export const compressFileSize = async (file: File): Promise<void> => {
	const baseSize = 1 * 1024 * 1024; // 1MB
	let quality: number = 1;
	if (file.size > baseSize) {
		quality = baseSize / file.size;
	}

	return new Promise((resolve, reject) => {
		new Compressor(file, {
			quality: quality,
			success: async (result: any) => {
				const pngBlob: any = new File([result], `image_${Date.now()}.png`, { type: 'image/png' });
				resolve(pngBlob);
			},
			error(err) {
				reject(err);
			},
		});
	});
};

export const calculateDateDifference = (start: string, end: string) => {
	const startDate = new Date(start);
	const endDate = end ? new Date(end) : new Date();

	let years = endDate.getFullYear() - startDate.getFullYear();
	let months = endDate.getMonth() - startDate.getMonth();

	if (months < 0) {
		years--;
		months += 12;
	}
	let returnString = '';
	if (years) returnString += `${years} years `;
	if (months) returnString += `${months} months `;
	return returnString;
};

export const generateUUID = () => {
	const cryptoObj = window.crypto || (window as any).msCrypto; // Support for older IE
	const bytes = new Uint8Array(16);
	cryptoObj.getRandomValues(bytes);

	// Set the UUID version to 4 and the variant bits (per RFC 4122)
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;

	// Convert the byte array to a UUID string format
	return Array.from(bytes)
		.map((b, i) => ([4, 6, 8, 10].includes(i) ? '-' : '') + b.toString(16).padStart(2, '0'))
		.join('');
};

export const setCookie = (name: string, value: any, days: any = '') => {
	let expires = '';
	if (days) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		expires = '; expires=' + date.toUTCString();
	}
	document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

export const getCookie = (name: string) => {
	const nameEQ = name + '=';
	const ca = document.cookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === ' ') c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
	}
	return null;
};

export const eraseCookie = (name: string) => {
	document.cookie = name + '=; Max-Age=-99999999;';
};

export const getBrowserID = () => {
	let browserID = getCookie('browserID');
	if (!browserID) {
		browserID = generateUUID();
		setCookie('browserID', generateUUID(), 15);
		return browserID;
	}
	return browserID;
};

export const getStaticPages = (data: any, slug: string) =>
	data?.find((res: any) => res.slug === slug);

export const getIdFromUrl = (url: string) => url.split('/').pop();

const getJobRolesArray = (roles: any, type: string) =>
	roles.map((item: any) => item[type].toString());

export const setInitialValueJobEdit = (data: any) => {
	const {
		title,
		countryId,
		stateId,
		jobType,
		cityId,
		levelId,
		industryId,
		company,
		description,
		salaryType,
		workMode,
		language,
		jobRole,
		skills,
		location,
		minAmount,
		maxAmount,
	} = data || {};

	const JobRoleArray = data && getJobRolesArray(jobRole, 'id');
	const skillsArray = data && getJobRolesArray(skills, 'id');
	const languageArray = data && getJobRolesArray(language, 'title');

	return {
		title,
		countryId,
		stateId,
		cityId,
		industryId,
		levelId,
		workMode,
		newRole: [],
		jobRole: JobRoleArray,
		language: languageArray,
		jobType,
		salaryType,
		company,
		location,
		skills: skillsArray,
		description,
		isJobBoost: false,
		minAmount,
		maxAmount,
	};
};

export const isObjectEmpty = (obj: any) => Object.keys(obj).length === 0;

export const formatLocation = (cityName: string, stateName: string, countryName: string) => {
	const locationParts = [];

	if (cityName) {
		locationParts.push(cityName);
	}
	if (stateName) {
		locationParts.push(stateName);
	}
	if (countryName) {
		locationParts.push(countryName);
	}

	return locationParts.join(', ');
};

export const formatDate = (dateString: string) => {
	const date = new Date(dateString);
	const day = date.getDate().toString().padStart(2, '0');
	const month = date.toLocaleString('default', { month: 'short' });
	const year = date.getFullYear();
	return `${day} ${month} ${year}`;
};

export const getPhoneFormat = (number: any) => {
	return /^(\d+(\.\d*)?)?$/.test(number);
};

export const removeDeletedExp = (data: any, index: any) => {
	const userData = [...data];
	if (index < 0 || index >= userData.length) {
		return userData;
	}

	userData.splice(index, 1);
	return userData;
};

export const validateString = (value: any) => {
	return onlySpecialCharsNotAllowRegex.test(value);
};

export const getSalaryRange = (data: any, key: any) => {
	if (!data[key] || data[key].length === 0) {
		return null;
	}

	const minSalary = data[key][0].id;
	const maxSalary = data[key][data[key].length - 1].id;

	return {
		minSalary,
		maxSalary,
	};
};
export const getImageAspectRatio = async (file: any) => {
	return new Promise((resolve, reject) => {
		const img: any = new Image();
		const reader = new FileReader();

		reader.onload = (event: any) => {
			img.src = event.target.result;
		};

		img.onload = () => {
			const width = img.width;
			const height = img.height;
			const aspectRatio = width / height;
			resolve(aspectRatio);
		};

		img.onerror = () => reject(new Error('Failed to load image'));
		reader.onerror = () => reject(new Error('Failed to read file'));

		reader.readAsDataURL(file);
	});
};

export const getFileNameByURL = (fileUrl: string) => {
	return fileUrl ? fileUrl.substring(fileUrl.lastIndexOf('/') + 1) : '';
};

export const downloadFile = async (fileUrl: string, fileName: string) => {
	const response = await fetch(fileUrl);
	const blob = await response.blob();
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', fileName);
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};

export const getFullDateFormat = (
	dateString: string,
	options: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	},
): string => {
	const date = dateString ? new Date(dateString) : '';
	return date ? date.toLocaleDateString('en-US', options) : '';
};

export const getFormattedPrice = (price: string, currency?: string) => {
	return `${Number(price || 0).toFixed(2)} ${currency}`;
};

export const checkObjHasValues = (obj: Record<string, any>): boolean => {
	if (obj === null || obj === undefined) {
		return false;
	}

	const keys = Object.keys(obj);

	return keys.some((key) => {
		const value = obj[key];
		return typeof value !== 'boolean' && value !== '' && value !== undefined && value !== null;
	});
};

export const convertPDFPageToImageBlob = async (canvas: any, pdfData: any) => {
	try {
		const page = await pdfData.getPage(1);
		const scale = 1.5; // You can adjust the scale as needed
		const viewport = page.getViewport({ scale });
		canvas.height = viewport.height;
		canvas.width = viewport.width;
		const renderContext = {
			canvasContext: canvas.getContext('2d'),
			viewport: viewport,
		};
		await page.render(renderContext).promise;
		const blob: any = await new Promise((resolve, reject) => {
			canvas.toBlob((blob: any) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error('Failed to convert canvas to Blob'));
				}
			}, 'image/png');
		});
		const imageName = `image_${Date.now()}.png`;
		blob.name = imageName;
		return blob;
	} catch (error: any) {
		throw new Error('Failed to render PDF page: ' + error.message);
	}
};

export const extractRelevantResumeFields = async (resumeData: any) => {
	try {
		const fieldsData: any = { ...resumeData };
		const finalData: any = {
			detailTitle: fieldsData.detailTitle,
			details: fieldsData.details,
			manageTemplateFields: fieldsData.manageTemplateFields,
		};

		for (const s_field of Object.keys(SAVING_FIELDS)) {
			const savingFields: any = SAVING_FIELDS[s_field];

			if (savingFields?.length) {
				for (const field of savingFields) {
					const fieldValue = fieldsData[field];

					if (fieldValue !== undefined) {
						// Check if fieldValue exists
						if (
							ARRAY_FIELDS.includes(field) &&
							Array.isArray(fieldValue) &&
							fieldValue.length > 0
						) {
							if (checkObjHasValues(fieldValue[fieldValue.length - 1])) {
								finalData[field] = fieldValue.slice(0, -1);
							}
						} else {
							finalData[field] = Array.isArray(fieldValue) ? fieldValue : fieldValue || ''; // Handle case for empty strings
						}
					}
				}
			}
		}

		return finalData;
	} catch (error) {
		console.error('Error filtering resume data:', error);
		return {}; // Return empty object in case of an error
	}
};

export const generateRandomString = (length: number) => {
	const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	const charactersLength = characters.length;
	let result = '';
	const randomValues = new Uint8Array(length);
	window.crypto.getRandomValues(randomValues);
	for (let i = 0; i < length; i++) {
		result += characters.charAt(randomValues[i] % charactersLength);
	}

	return result;
};

export const convertToPng: any = async (fileUrl: string) => {
	try {
		if (fileUrl.toLowerCase().endsWith('.png')) {
			return fileUrl;
		}

		const response = await fetch(fileUrl);
		const blob = await response.blob();

		const canvas: any = createCanvas(1, 1);
		const context: any = canvas.getContext('2d');

		if (!context) {
			console.error('Unable to create canvas context');
			return null;
		}

		const img = await loadImage(URL.createObjectURL(blob));
		canvas.width = img.width;
		canvas.height = img.height;
		context.drawImage(img, 0, 0);

		return new Promise((resolve, reject) => {
			canvas.toBlob((pngBlob: any) => {
				if (pngBlob) {
					resolve(URL.createObjectURL(pngBlob));
				} else {
					reject(new Error('Conversion to PNG failed'));
				}
			}, 'image/png');
		});
	} catch (error) {
		console.error('Error converting file to PNG:', error);
		return null;
	}
};

export const LANGUAGE_PROFICIENCY = (translate: any) => {
	return [
		{ value: '', label: translate('PlaceHolder.selectProficiency') },
		{ value: 'BEGINNER', label: 'Beginner' },
		{ value: 'MODERATE', label: 'Moderate' },
		{ value: 'ADVANCED', label: 'Advanced' },
	];
};

export const calculateTimeSpan = (
	startDateString: string,
	endDateString: string,
	currentlyWorking: boolean,
	type: boolean,
	format?: string,
): string | undefined => {
	if (!startDateString || (startDateString && !currentlyWorking && !endDateString)) {
		return undefined;
	}

	const startDateParts: number[] = startDateString.split('-').map(Number);
	const endDateParts: number[] = currentlyWorking
		? [new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate()]
		: endDateString.split('-').map(Number);

	const startDate: Date = new Date(startDateParts[0], startDateParts[1] - 1, startDateParts[2]);
	const endDate: Date = currentlyWorking
		? new Date()
		: new Date(endDateParts[0], endDateParts[1] - 1, endDateParts[2]);

	const startYear: number = startDate.getFullYear();
	const startMonth: number = startDate.getMonth();
	const endYear: number = endDate.getFullYear();
	const endMonth: number = endDate.getMonth();

	const years: number = endYear - startYear;
	const months: number = endMonth - startMonth;

	let yearString: string = '';
	let monthString: string = '';

	if (years > 0) {
		yearString = type ? `${years}yr ` : '';
	}

	if (months > 0) {
		monthString = type ? `${months === 1 ? '1 month' : `${months} months`}` : '';
	}

	if (format === 'yy-yy') {
		return `${startYear}-${endYear}`;
	}

	const formattedStartDate: string = `${startDate.toLocaleDateString('en-US', { month: 'short' })} ${startYear}`;
	const formattedEndDate: string = currentlyWorking
		? 'Present'
		: `${endDate.toLocaleDateString('en-US', { month: 'short' })} ${endYear}`;

	const timeSpan: string = `${formattedStartDate} - ${formattedEndDate} ${(yearString ?? monthString) ? `- ${yearString}${monthString}` : ''}`;

	return timeSpan.trim();
};

export const extractTagsAndMentions = async (input: any) => {
	// Regex to match @user mentions and hashtags with IDs
	const userMentionRegex = /@\[(.*?)\]\((\d+)\)/g;
	const hashtagRegex = /#\[(.*?)\](?:\((\d+)\))?|#(\w+)/g;
	// Arrays to store user mentions and hashtags
	const taggedUsers = [];
	const hashTags = [];
	// Process user mentions
	let match;
	while ((match = userMentionRegex.exec(input)) !== null) {
		const [, name, id] = match;
		taggedUsers.push({ userId: Number(id), name });
	}
	// Process hashtags
	while ((match = hashtagRegex.exec(input)) !== null) {
		if (match[1] !== undefined) {
			// Handle hashtags with ID
			const [, name, id] = match;
			hashTags.push({ id: id || '0', name });
		} else if (match[3] !== undefined) {
			// Handle hashtags without ID
			const name = match[3];
			hashTags.push({ id: '0', name });
		}
	}
	return { taggedUsers, hashTags };
};

export const extractPostData = (content: string) => {
	const hashTags = new Set();
	const newHashTags = new Set();
	const taggedUsers = new Set();

	// Combined regex to match hashtags and mentions
	const combinedRegex = /@\[([^\]]+)\]\(([^)]+)\)|#\[([^\]]+)\]\(([^)]+)\)/g;

	let match;

	// Extract hashtags and mentions
	while ((match = combinedRegex.exec(content)) !== null) {
		if (match[1] !== undefined) {
			// This is a mention
			const name = match[1];
			const userId = parseInt(match[2], 10);
			taggedUsers.add(JSON.stringify({ userId, name }));
		} else if (match[3] !== undefined) {
			// This is a hashtag
			const display = match[3]; // This is the name, not ID
			const id = match[4]; // This is the ID
			if (id.startsWith('new-')) {
				newHashTags.add(display);
			} else {
				hashTags.add(id); // Store the ID for normal hashtags
			}
		}
	}

	return {
		hashTags: Array.from(hashTags),
		newHashTags: Array.from(newHashTags),
		taggedUsers: Array.from(taggedUsers).map((user: any) => JSON.parse(user)),
	};
};

export const showExpInOrder = (data: any) => {
	return data.sort((a: any, b: any) => {
		const dateA = a.endDate || a.startDate;
		const dateB = b.endDate || b.startDate;

		// If endDate is empty, push the item to the top
		if (a.endDate === '' && b.endDate !== '') return -1;
		if (a.endDate !== '' && b.endDate === '') return 1;

		// Convert the dates to a sortable format
		const dateAParsed: any = new Date(dateA);
		const dateBParsed: any = new Date(dateB);

		// Sort descending by date
		return dateBParsed - dateAParsed;
	});
};

export const getMinDate = () => {
	const today = new Date();
	return new Date(today.setFullYear(today.getFullYear() - 100));
};

export const sliceString = (title: string, from: number) => {
	return title.slice(from);
};

export const formatShortDate = (dateString: string): string => {
	try {
		if (!dateString) {
			return 'Present';
		}

		const date = new Date(dateString);

		// More robust date validation using Number.isNaN
		if (Number.isNaN(date.getTime())) {
			throw new Error('Invalid date format');
		}

		const options: Intl.DateTimeFormatOptions = {
			month: 'short',
			year: 'numeric',
		};

		// Format date and replace all spaces with hyphens
		return date.toLocaleDateString('en-US', options).replace(/ /g, '-').trim();
	} catch (error: any) {
		console.error('Error formatting date:', error.message);
		return 'Present'; // Fallback return value in case of errors
	}
};

export const getReportReasonKey = (key: any) => {
	switch (key) {
		case 'communityComment':
			return { title: t('Report.comment'), value: 'commentReportReasons' };
		case 'socialPostCommentReply':
			return { title: t('Report.socialCommentReply'), value: 'commentReportReasons' };
		case 'socialPostComment':
			return { title: t('Report.socialComment'), value: 'commentReportReasons' };
		case 'community':
			return { title: t('Report.post'), value: 'communityReportReasons' };
		case 'socialHashtag':
			return { title: t('Report.hashtag'), value: 'hashTagReportReasons' };
		case 'socialStory':
			return { title: t('Report.story'), value: 'socialReportReasons' };
		case 'socialPost':
		case 'post': // Fall-through case to reduce duplication
			return { title: t('Report.post'), value: 'socialReportReasons' };
		default:
			return { title: t('Report.profile'), value: 'userReportReasons' };
	}
};

// Create Thumbnail by base64 video url
export const createThumbnailByVideo = (base64VideoUrl: string): Promise<string> => {
	return new Promise((resolve, reject) => {
		const video = document.createElement('video');
		video.src = base64VideoUrl;

		video.addEventListener('loadeddata', () => {
			// Seek to 1 second (or any specific time you want)
			video.currentTime = 1;

			video.addEventListener('seeked', () => {
				// Create a canvas element dynamically
				const canvas = document.createElement('canvas');

				// Set the canvas dimensions to match the video frame
				canvas.width = video.videoWidth;
				canvas.height = video.videoHeight;

				const ctx = canvas.getContext('2d');
				if (ctx) {
					// Draw the current video frame to the canvas
					ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

					// Convert the canvas to a Base64 image and resolve the promise
					const thumbnailUrl = canvas.toDataURL('image/jpeg');
					resolve(thumbnailUrl);
				} else {
					reject('Unable to get canvas context');
				}
			});

			video.addEventListener('error', () => {
				reject('Video loading error');
			});
		});
	});
};

export const formatTime = (isoString: string): string => {
	return new Date(isoString).toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
};

export const increaseFollowing = (following: any) => {
	updateUserInfo({ followings: Number(following) + 1 });
};

export const decreaseFollowing = (following: any) => {
	updateUserInfo({ followings: Number(following) - 1 });
};

export const getFileName = (url: string) => {
	const baseName = url?.substring(0, url.lastIndexOf('.')); // Remove the extension
	return `${baseName}-00001-thumb.png`;
};

export const handleAsyncOperation = async (
	asyncFunc: () => Promise<any>,
	finallyFunc?: () => void,
) => {
	try {
		await asyncFunc();
	} catch (error) {
		console.error('An error occurred: ', error);
	} finally {
		if (finallyFunc) finallyFunc();
	}
};

export const getTodayDate = () => {
	const today = new Date();
	const yyyy = today.getFullYear();
	const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
	const dd = String(today.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

export const buildBlogApiUrl = (currentPage: number, filters: any, dataLimits: string = '10') => {
	const params = new URLSearchParams();
	params.set('populate', '*');
	params.set('sort[publishedAt]', 'desc');
	params.set('pagination[page]', currentPage.toString());
	params.set('pagination[pageSize]', dataLimits);

	if (filters?.categoryId) {
		params.set('filters[category][id][$eq]', filters.categoryId);
	}

	if (filters?.keyword) {
		params.set('filters[Title][$containsi]', filters.keyword);
	}

	if (filters?.featuredBlog) {
		params.set('filters[FeaturedBlog]', filters.featuredBlog);
	}
	if (filters?.blogIdNotEqual) {
		params.set('filters[id][$ne]', filters.blogIdNotEqual);
	}

	return params.toString();
};

export const destructurePayload = (payloadData: any) => {
	const { data, notification } = payloadData || {};
	return {
		title: notification?.title,
		body: notification?.body,
		type: data?.type,
		id: data?.id,
	};
};

export const getNotificationURL = async (
	type: string = '',
	id: number | null = null,
	roomId: number | null = null,
	user_type: string = '',
	redirectOn: string | null = '',
) => {
	try {
		if (id === null) {
			throw new Error('ID is required for notification URLs.');
		}

		const basePathMap: Record<string, string> = {
			applyJob: `/job-detail/${id}`,
			scheduleInterview: `/${user_type === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/scheduled-meetings`,
			scheduleInterviewReminder: `/${user_type === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/scheduled-meetings`,
			meetingCancel: `/${user_type === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/scheduled-meetings`,
			socialPostLike: `/feed-detail/${id}`,
			postComment: `/feed-detail/${id}`,
			tagOnSocialPostComment: `/feed-detail/${id}`,
			tagOnSocialPost: `/feed-detail/${id}`,
			postCommentLike: `/feed-detail/${id}`,
			shareSocialPost: `/feed-detail/${id}`,
			applyJobStatusChange: `/job-detail/${id}`,
			subscriptionReminder: `/current-plan-history`,
			paymentSuccess: `/current-plan-history`,
			paymentFailed: `/current-plan-history`,
			cancelSubscription: `/current-plan-history`,
		};

		if (basePathMap[type]) {
			return basePathMap[type];
		}

		if (type === 'connectionRequest') {
			if (!user_type) {
				throw new Error('User type is required for connection request URLs.');
			}
			return `/${user_type === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/profile/${id}`;
		}

		if (type === 'chat') {
			if (roomId === null) {
				throw new Error('Room ID is required for chat URLs.');
			}
			return `/chat/${id}/${roomId}`;
		}

		if (type === 'tagOnSocialPost') {
			return `/feed-detail/${id}`;
		}
		if (type === 'postCommentLike') {
			return `/feed-detail/${id}`;
		}
		if (type === 'followCompany') {
			return `/${redirectOn || user_type}/profile/${id}`;
		}

		return '';
	} catch (error) {
		console.error('Error in getNotificationURL: ', error);
		return '';
	}
};

export const isTimeGone = (timeString: string): boolean => {
	// Check if timeString is a valid string
	if (typeof timeString !== 'string') {
		throw new TypeError('Expected a string input');
	}

	// Get today's current date and time
	const now = new Date();
	now.setSeconds(0); // Optional: reset seconds for a cleaner comparison

	// Extract start and end times from the string
	const [, endTime] = timeString.split('-').map((time) => time.trim());

	// Function to create a Date object for a specific time on today's date
	const createDateForTime = (time: string): Date => {
		const [timePart, modifier] = time.split(' ');
		const [hoursPart, minutesPart] = timePart.split(':').map(Number);

		let hours = hoursPart;
		const minutes = minutesPart || 0;

		// Adjust hours for AM/PM
		if (modifier.toLowerCase() === 'pm' && hours < 12) {
			hours += 12;
		} else if (modifier.toLowerCase() === 'am' && hours === 12) {
			hours = 0;
		}

		// Define the date with the specific hours and minutes
		return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
	};

	// Create Date objects for start and end times
	const endDate = createDateForTime(endTime);

	// Check if the current time is outside the range of start and end times
	return now > endDate;
};

export const breakTheName = (fullName: string) => {
	const name = fullName.split(' ');
	const firstName = name[0];
	const lastName = name.slice(1).join(' ');
	return [firstName, lastName];
};

export const languageCode: any = {
	English: 'en',
	Hindi: 'hi',
	Turkish: 'tr',
	Spanish: 'es',
	German: 'de',
};

export const formatAvailableDates = (dates: any, translate: any) => {
	// Filter dates for the current month and format them
	const availableDates = dates
		.map((date: any) => new Date(date))
		// .filter((date: any) => date.getMonth() + 1 === currentMonth && date.getFullYear() === currentYear)
		.map((date: any) => date.getDate());

	// Format the available dates into the desired string
	if (availableDates.length === 0) {
		return translate('Common.noAvailableDatesThisMonth');
	}

	const lastDate = availableDates.pop();
	const formattedDates =
		availableDates.length > 0 ? `${availableDates.join(', ')} and ${lastDate}` : `${lastDate}`;
	return translate('Common.availableOn', { date: formattedDates });
};

export const reasonOfReport = (reason: string) => {
	return reason;
};

export const dateFormatForNotifications = (dateString: any) => {
	const date = moment(dateString);
	const now = moment();

	// Check if the date is today
	if (date.isSame(now, 'day')) {
		return `Today at ${date.format('hh:mm A')}`;
	}

	// Check if the date is tomorrow
	if (date.isSame(now.clone().add(1, 'day'), 'day')) {
		return `Tomorrow at ${date.format('hh:mm A')}`;
	}

	// Check if the date is yesterday
	if (date.isSame(now.clone().subtract(1, 'day'), 'day')) {
		return `Yesterday at ${date.format('hh:mm A')}`;
	}

	// For any other date, format as desired
	return date.format('MMMM D, YYYY [at] hh:mm A'); // Example: October 15, 2024 at 11:42 PM
};

export const getCurrencySymbol = (currencyCode: string) => {
	return (0)
		.toLocaleString(undefined, {
			style: 'currency',
			currency: currencyCode,
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})
		.replace(/\d/g, '')
		.trim();
};

export const getDaysDifference = (expiryDate: string): number => {
	try {
		// Check if expiry date is valid
		if (!moment(expiryDate, moment.ISO_8601, true).isValid()) {
			throw new Error('Invalid date format');
		}

		const expiry = moment(expiryDate).startOf('day'); // Set expiry date to start of day
		const currentDate = moment().startOf('day'); // Set current date to start of day
		const dayDiff = expiry.diff(currentDate, 'days');

		return dayDiff + 1;
	} catch {
		return -1; // Return -1 in case of error
	}
};

export const getCommonObjects = (arr1: any, arr2: any) => {
	// Create a Set of IDs from the second array for quick lookup
	const idSet = new Set(arr2.map((item: any) => item.id));

	// Filter the first array and extract the common IDs
	return arr1.filter((item: any) => idSet.has(item.id)).map((item: any) => item.id);
};

export const convertJpegToPng = (jpegFile: File): Promise<Blob> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const reader = new FileReader();

		reader.onload = (e) => {
			img.src = e.target?.result as string;
		};

		reader.onerror = () => reject('Failed to read image');

		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;

			const ctx = canvas.getContext('2d');
			if (!ctx) return reject('Canvas context not available');

			ctx.drawImage(img, 0, 0);
			canvas.toBlob((blob) => {
				if (blob) {
					resolve(blob); // Return the PNG blob
				} else {
					reject('Failed to convert image to PNG');
				}
			}, 'image/png');
		};

		img.onerror = () => reject('Failed to load image');
		reader.readAsDataURL(jpegFile); // Read the JPEG data to trigger conversion
	});
};

export const getRoomIdFromUrl = (windowURL: any) => {
	if (windowURL) {
		const pathParts = windowURL.split('/');
		return pathParts[pathParts.length - 1];
	}
};

export const clearStorageExceptPrefix = (storage: Storage, prefixes: string[]): void => {
	for (const key of Object.keys(storage)) {
		if (!prefixes.some((prefix) => key.startsWith(prefix))) {
			storage.removeItem(key);
		}
	}
};

export const createImage = (url: string) =>
	new Promise<HTMLImageElement>((resolve, reject) => {
		const image = new Image();
		image.addEventListener('load', () => resolve(image));
		image.addEventListener('error', (error) => reject(error));
		image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
		image.src = url;
	});

export function getRadianAngle(degreeValue: number) {
	return (degreeValue * Math.PI) / 180;
}

export function rotateSize(width: number, height: number, rotation: number) {
	const rotRad = getRadianAngle(rotation);

	return {
		width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
		height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
	};
}

export async function getCroppedImg(
	imageSrc: string,
	pixelCrop: { x: number; y: number; width: number; height: number },
	rotation = 0,
	flip = { horizontal: false, vertical: false },
): Promise<string | null> {
	const image = await createImage(imageSrc);
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		return null;
	}

	const rotRad = getRadianAngle(rotation);

	const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

	canvas.width = bBoxWidth;
	canvas.height = bBoxHeight;

	ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
	ctx.rotate(rotRad);
	ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
	ctx.translate(-image.width / 2, -image.height / 2);

	ctx.drawImage(image, 0, 0);

	const croppedCanvas = document.createElement('canvas');
	const croppedCtx = croppedCanvas.getContext('2d');

	if (!croppedCtx) {
		return null;
	}

	croppedCanvas.width = pixelCrop.width;
	croppedCanvas.height = pixelCrop.height;

	croppedCtx.drawImage(
		canvas,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		pixelCrop.width,
		pixelCrop.height,
	);

	return new Promise((resolve) => {
		croppedCanvas.toBlob((file: any) => {
			resolve(URL.createObjectURL(file));
		}, 'image/png');
	});
}

export async function getRotatedImage(imageSrc: string, rotation = 0): Promise<string> {
	const image = await createImage(imageSrc);
	const canvas = document.createElement('canvas');
	const ctx: any = canvas.getContext('2d');

	const orientationChanged =
		rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270;
	if (orientationChanged) {
		canvas.width = image.height;
		canvas.height = image.width;
	} else {
		canvas.width = image.width;
		canvas.height = image.height;
	}

	ctx.translate(canvas.width / 2, canvas.height / 2);
	ctx.rotate((rotation * Math.PI) / 180);
	ctx.drawImage(image, -image.width / 2, -image.height / 2);

	return new Promise((resolve) => {
		canvas.toBlob((file: any) => {
			resolve(URL.createObjectURL(file));
		}, 'image/png');
	});
}
